/**
 * Returns the Album or AlbumFolder (entity) of an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupEntity = albumMembershipGroup => {
  return albumMembershipGroup?.album ?? albumMembershipGroup?.album_folder;
};

/**
 * Returns whether the AlbumMembershipGroup is social share enabled.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {boolean}
 */
export const albumMembershipGroupHasSocialShareEnabled = albumMembershipGroup => {
  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);

  return entity?.social_share_enabled ?? false;
};

/**
 * Returns the AlbumMembershipGroup's hashtag.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {string}
 */
export const getAlbumMembershipGroupHashcode = albumMembershipGroup => {
  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);

  return entity?.hash_code ?? '';
};

/**
 * Returns whether an AlbumMembershipGroup should show
 * contributor attribution on each photo.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {boolean}
 */
export const albumMembershipGroupAllowsPublicPhotoAttribution = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);

  return !!entity?.display_public_photo_attribution;
};

/**
 * Determines if the given membership is for a Picture Day Album Folder
 *
 * @param {Object} albumMembershipGroup the album membership group
 * @returns {boolean|*|boolean}
 */
export const isPictureDayMembership = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (albumMembershipGroup.is_album) {
    return albumMembershipGroup.album?.root_album_folder?.picture_day_enabled;
  }

  return albumMembershipGroup.album_folder?.picture_day_enabled;
};

/**
 * Determines if the given membership is for a Web Gallery Album Folder
 *
 * @param {Object} albumMembershipGroup the album membership group
 * @returns {boolean|*|boolean}
 */
export const isWebGalleryMembership = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (albumMembershipGroup.is_album) {
    return albumMembershipGroup.album?.root_album_folder?.is_web_gallery;
  }

  return albumMembershipGroup.album_folder?.is_web_gallery;
};

/**
 * Determines if the given membership is for a Picture Day Album Folder
 *
 * @param {Object} albumMembershipGroup the album membership group
 * @returns {boolean|*|boolean}
 */
export const isYearbookMembership = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (
    albumMembershipGroup.is_album &&
    !albumMembershipGroup.album?.pricing_categories
  ) {
    return !!(
      albumMembershipGroup.album?.root_album_folder?.pricing_categories
        ?.records ?? []
    ).find(cat =>
      (cat?.pricing_category?.name ?? '').includes('Including Yearbook Export'),
    );
  }

  return !!(
    albumMembershipGroup.album_folder?.pricing_categories?.records ?? []
  ).find(cat =>
    (cat?.pricing_category?.name ?? '').includes('Including Yearbook Export'),
  );
};

/**
 * Determines if the given membership allows multi pose print packages
 *
 * @param {Object} albumMembershipGroup the album membership group
 * @returns {boolean|*|boolean}
 */
export const isMultiPosePackageMembership = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (albumMembershipGroup.is_album) {
    return albumMembershipGroup.album?.root_album_folder
      ?.multi_pose_print_packages_enabled;
  }

  return albumMembershipGroup.album_folder?.multi_pose_print_packages_enabled;
};

/**
 * Determines if the given membership allows multi identity print packages
 *
 * @param {Object} albumMembershipGroup the album membership group
 * @returns {boolean|*|boolean}
 */
export const isMultiIdentityPackageMembership = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (albumMembershipGroup.is_album) {
    return albumMembershipGroup.album?.root_album_folder
      ?.multi_identity_print_packages_enabled;
  }

  return albumMembershipGroup.album_folder
    ?.multi_identity_print_packages_enabled;
};

/**
 * Determines if the given membership allows multi identity digital bundles
 *
 * @param {Object} albumMembershipGroup the album membership group
 * @returns {boolean|*|boolean}
 */
export const isMultiIdentityBundleMembership = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (albumMembershipGroup.is_album) {
    return albumMembershipGroup.album?.root_album_folder
      ?.multi_identity_digital_bundles_enabled;
  }

  return albumMembershipGroup.album_folder
    ?.multi_identity_digital_bundles_enabled;
};

/**
 * Returns the Creator of an albumMembershipGroup
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupEntityAccount = albumMembershipGroup => {
  const account =
    albumMembershipGroup?.album?.creator ??
    albumMembershipGroup?.album?.account ??
    albumMembershipGroup?.album_folder?.creator ??
    albumMembershipGroup?.album_folder?.account;

  return account;
};
